/**
 * Media url rules shared with the Android app (mobile/src/services/mediaUrl.ts).
 *
 * A photo url is stored with whatever address the uploading device used (a
 * LAN address, an old tunnel …). Only the key matters, so every Kindra media
 * url in a response is pointed at the API this app is talking to. Private
 * identity documents are never touched.
 */
const MEDIA_URL =
  /^https?:\/\/[^/\s]+\/media\/(?:file|local)\/((?!identity-)[A-Za-z0-9][A-Za-z0-9.-]*)$/;
/** Local (test and development) storage returns the path alone. */
const RELATIVE_MEDIA_URL = /^\/media\/(?:file|local)\/((?!identity-)[A-Za-z0-9][A-Za-z0-9.-]*)$/;

export function rebaseMediaUrls<T>(value: T, apiUrl: string): T {
  if (typeof value === 'string') {
    const match = MEDIA_URL.exec(value) ?? RELATIVE_MEDIA_URL.exec(value);
    return (match ? `${apiUrl}/media/file/${match[1]}` : value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => rebaseMediaUrls(item, apiUrl)) as T;
  }
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, rebaseMediaUrls(item, apiUrl)]),
    ) as T;
  }
  return value;
}

/** The copy widths the server hands out; asking only for these keeps its resized copies well reused. */
const THUMBNAIL_WIDTHS = [64, 128, 192, 256, 384, 512, 768, 1024] as const;

/** A server-resized copy of one of Kindra's own pictures, at least `pixels` wide. Other urls are returned as they are. */
export function thumbnailUrl(url: string | undefined, pixels: number): string | undefined {
  if (!url || !MEDIA_URL.test(url)) {
    return url;
  }
  const width =
    THUMBNAIL_WIDTHS.find((candidate) => candidate >= pixels) ??
    THUMBNAIL_WIDTHS[THUMBNAIL_WIDTHS.length - 1];
  return `${url}?w=${width}`;
}
